/* eslint-disable react-native/no-inline-styles */
import * as React from "react";
import { Divider, IconButton, Menu, Subheading, TextInput, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText } from "app/shared/components/form/input";
import { StyleSheet } from "react-native";
import { StackActions, useNavigation } from "@react-navigation/native";
import ScreenNameType from "app/shared/constant/nav-screen.type";
import { ACCOUNT } from "app/shared/services/services-model.type";
import { formatNumberThousand } from "app/shared/util/format-currency";
import moment from "moment";
import { formatFdTerm, getIntRate, getTermDepositProductName } from "../../term-deposit-utils/term-deposit.utils";
import { getAccountStatus } from "app/shared/util/common";
import { setTermTopUpReset } from "../../term-deposit-top-up/reducer/actions";
import { useDispatch, useSelector } from "react-redux";
import { setTermStatementReset } from "../../term-deposit-statement/reducer/actions";
import { pushCleverTapEvent } from "app/shared/util/app-flyer";
import { getContactIdSelector } from "app/modules/account-management/reducer/selectors";

export interface FormTermDepositDetailsProps {
  data?: ACCOUNT;
}

enum FormEnum {
  bookNo,
  productName,
  branchName,
  currentAmount,
  interestsRate,
  term,
  openDate,
  matDate,
  topUpDateLasted,
  status,
  ib,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormTermDepositDetailsData = {
  bookNo: string;
  productName: string;
  branchName: string;
  currentAmount: string;
  interestsRate: string;
  term: string;
  openDate: string;
  matDate: string;
  topUpDateLasted: string;
  status: string;
};

const FormTermDepositDetails = (props: FormTermDepositDetailsProps) => {
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const { dispatch } = useNavigation();
  const dispatchStore = useDispatch();
  const isDesktop = useDesktopMediaQuery();
  const [visible, setVisible] = React.useState(false);
  const { data } = props;

  const contactId = useSelector(getContactIdSelector);

  // Instance Form Method
  const { control, setValue } = useForm<FormTermDepositDetailsData>({
    mode: "onChange",
  });

  const handlerShowToUp = React.useCallback(() => {
    if (data?.IB === "Y" && data?.CONTENT?.length && data.CONTENT.substring(0, 1) === "Y") return true;
    return false;
  }, [data?.CONTENT, data?.IB]);

  const handlerShowClose = React.useCallback(() => {
    if (data?.IB === "Y" && data?.CONTENT?.length && (data.CONTENT.substring(1, 2) === "Y" || data.CONTENT.substring(2, 3) === "Y"))
      return true;
    return false;
  }, [data?.CONTENT, data?.IB]);

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          textAlign: isDesktop ? "center" : "left",
          color: colors.text,
          width: 200,
          ...fonts.semibold,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { alignSelf: "center", backgroundColor: colors.accent },
        displayTopUpButton: { display: handlerShowToUp() === true ? "flex" : "none" },
        displayCloseButton: { display: handlerShowClose() === true ? "flex" : "none" },
        menuStyle: { borderRadius: spacing.small },
      }),
    [colors.accent, colors.notification, colors.text, fonts.semibold, handlerShowClose, handlerShowToUp, isDesktop],
  );

  const eventPushCleverTap = (id: string, cta?: string) => {
    const { eventNameCT, paramsCT } = handlerParamsCleverTap(id, cta);
    pushCleverTapEvent(eventNameCT, paramsCT);
  };

  const openMenu = () => setVisible(true);

  const closeMenu = () => setVisible(false);

  const onPressTermClose = () => {
    if (visible) closeMenu();
    eventPushCleverTap(data?.ACCOUNTNO || "", "Tất toán");
    dispatchStore(setTermStatementReset());
    dispatch(StackActions.replace(ScreenNameType.TERM_DEPOSIT_STATEMENT_CREATE, { account: data, resetData: true }));
  };

  const onPressTermTopUp = () => {
    if (visible) closeMenu();
    eventPushCleverTap(data?.ACCOUNTNO || "", "Nộp thêm");
    dispatchStore(setTermTopUpReset());
    dispatch(StackActions.replace(ScreenNameType.TERM_DEPOSIT_TOP_UP_CREATE, { account: data, resetData: true }));
  };

  const getListMenu = () => {
    const list: Array<{ label: string; action: () => void }> = [];
    if (handlerShowToUp()) list.push({ label: t("SAVING_NOP_THEM"), action: () => onPressTermTopUp() });
    if (handlerShowClose()) list.push({ label: t("SAVING_TAT_TOAN"), action: () => onPressTermClose() });
    return list;
  };

  const onPressHome = () => {
    eventPushCleverTap(data?.ACCOUNTNO || "", "Trang chủ");
    dispatch(StackActions.replace(ScreenNameType.HOME));
  };

  const handlerParamsCleverTap = (termId: string, cta?: string) => {
    console.log("handlerParamsCleverTap");
    //Nộp thêm | Tất toán | Trang chủ
    let param = {};
    param = {
      ct_channel: "ib_rb",
      ct_cccd_ID: contactId,
      ct_list_deposit_status: "success",
      ct_deposit_ID: termId,
    };
    if (cta) {
      param = {
        ...param,
        ct_cta: cta || "",
      };
    }

    return {
      eventNameCT: "ct_list_of_deposit",
      paramsCT: param,
    };
  };

  const rightAmount = <TextInput.Affix textStyle={fonts.regular} text={t("COMMON_VND")} />;

  /// MARK: - Child Component
  const FieldForm = () => {
    const listMenu = getListMenu();
    return (
      <Block row={!isDesktop} center ph={isDesktop ? spacing.huge : 0} pv={isDesktop ? 0 : spacing.massive}>
        <StackSpacing size="medium" />
        <Subheading numberOfLines={2} style={styles.fieldFromText}>
          {t("SAVING_CHI_TIET_SO_TIET_KIEM")}
        </Subheading>
        <StackSpacing size="medium" />
        <>
          {!isDesktop && listMenu.length > 0 && (
            <Block flex={1} right absRight={spacing.micro}>
              <Menu
                contentStyle={styles.menuStyle}
                visible={visible}
                onDismiss={closeMenu}
                anchor={<IconButton onPress={openMenu} icon="menu-open" size={24} hasTVPreferredFocus={undefined} tvParallaxProperties={undefined} />}>
                {listMenu.map((item, index) => {
                  return (
                    <Block key={index}>
                      <Menu.Item titleStyle={{ ...fonts.medium }} onPress={item.action} title={item.label} />
                      <Divider style={{ display: index !== listMenu.length - 1 ? "flex" : "none" }} />
                    </Block>
                  );
                })}
              </Menu>
            </Block>
          )}
        </>
        <Block display={isDesktop ? "flex" : "none"} flex={1} middle>
          <Button
            style={styles.displayTopUpButton}
            uppercase={false}
            labelStyle={{ ...fonts.medium }}
            mode="contained"
            onPress={onPressTermTopUp}>
            {t("SAVING_NOP_THEM")}
          </Button>
          <StackSpacing size={handlerShowClose() === true ? "huge" : "none"} />
          <Button
            style={styles.displayCloseButton}
            uppercase={false}
            labelStyle={{ ...fonts.medium }}
            mode="contained"
            onPress={onPressTermClose}>
            {t("SAVING_TAT_TOAN")}
          </Button>
          <StackSpacing size="medium" />
        </Block>
      </Block>
    );
  };

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="productName" label={t("SAVING_SAN_PHAM")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="openDate" label={t("SAVING_NGAY_MO_SO")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText
            control={control}
            inputProps={{ editable: false, right: rightAmount }}
            name="currentAmount"
            label={t("SAVING_SO_DU_SO")}
          />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="matDate" label={t("SAVING_NGAY_DEN_HAN")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="bookNo" label={t("SAVING_SO_SO")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="branchName" label={t("ACC_CHI_NHANH_QL_SO")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="interestsRate" label={t("SAVING_LAI_SUAT_NOP_GAN_NHAT")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="term" label={t("SAVING_KY_HAN")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="topUpDateLasted" label={t("SAVING_NGAY_NOP_GAN_NHAT")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="status" label={t("ACC_TRANG_THAI")} />
        </Block>
      </Block>
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <QueueSpacing size="large" />
      <Button onPress={onPressHome} labelStyle={{ color: colors.whiteText }} mode="contained" style={styles.acceptButtonContainer}>
        {t("FUNC_TRANG_CHU")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (data?.ACCOUNTNO) {
      setValue("bookNo", data.ACCOUNTNO || "");
      setValue("currentAmount", formatNumberThousand(data.CURRENTBALANCE));
      setValue("interestsRate", getIntRate(data.INTRATE) + " % / " + t("COMMON_NAM").toLowerCase());
      if (data.OPENDATE?.length) setValue("openDate", moment(String(data.OPENDATE)).format("DD/MM/YYYY").toString());
      if (data.MATDATE?.length) setValue("matDate", moment(String(data.MATDATE)).format("DD/MM/YYYY").toString());
      setValue("productName", getTermDepositProductName(data.PRODUCTCODE));
      setValue("branchName", data.BRANCH_NAME || "");
      setValue("status", getAccountStatus(data.STATUS));
      setValue("term", formatFdTerm(data.FDTERM));
      if (data.LASTTRANSDATE?.length) setValue("topUpDateLasted", moment(String(data.LASTTRANSDATE)).format("DD/MM/YYYY").toString());
      eventPushCleverTap(data?.ACCOUNTNO || "", 'Chi tiết');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setValue, t]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm()}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTermDepositDetails;
